import React, { useState, useEffect, useRef } from "react";
import {
    Grid,
    Paper,
    Button,
    Box,
    TextField,
    Autocomplete,
    Collapse,
    LinearProgress,
    Typography,
    Chip,
    Skeleton
} from "@mui/material";
import { 
    Clear as ClearIcon
} from "@mui/icons-material";
import axiosInstance from "../AxiosInstance";

const ProductFilters = ({ 
    selectedForecastId, 
    selectedForecastPeriodId, 
    selectedBrands, 
    setSelectedBrands,
    selectedProductGroups, 
    setSelectedProductGroups,
    selectedProductIds, 
    setSelectedProductIds,
    filtersExpanded,
    onToggle,
    selectedCustomerId,
    selectedChannelId,
    useOrganisationProducts = false,
    productsUrl,
    onProductsLoaded
}) => {
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableProductGroups, setAvailableProductGroups] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingProductGroups, setLoadingProductGroups] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [filterMaps, setFilterMaps] = useState(null);
    const productsRequestController = useRef(null);

    const hasActiveFilters = () => {
        return selectedBrands.length > 0 || 
               selectedProductGroups.length > 0 || 
               selectedProductIds.length > 0;
    };

    const clearAllFilters = () => {
        setSelectedBrands([]);
        setSelectedProductGroups([]);
        setSelectedProductIds([]);
    };

    const clearBrands = () => {
        setSelectedBrands([]);
    };

    const clearProductGroups = () => {
        setSelectedProductGroups([]);
    };

    const clearSkus = () => {
        setSelectedProductIds([]);
    };

    const removeFilter = (type, value) => {
        switch (type) {
            case 'brand':
                setSelectedBrands(prev => prev.filter(b => b !== value));
                break;
            case 'productGroup':
                setSelectedProductGroups(prev => prev.filter(pg => pg !== value));
                break;
            case 'product':
                setSelectedProductIds(prev => prev.filter(id => id !== value));
                break;
            default:
                break;
        }
    };

    const isFilterDataLoading = () => {
        return loadingBrands || loadingProductGroups || loadingProducts;
    };

    const createFilterMaps = (products) => {
        const brandToGroups = new Map();
        const brandToProducts = new Map();
        const groupToBrands = new Map();
        const groupToProducts = new Map();
        const productToBrands = new Map();
        const productToGroups = new Map();

        products.forEach(product => {
            const { productId, brand, groupName } = product;
            const cleanBrand = brand && brand.trim() !== '' ? brand : null;
            const cleanGroup = groupName && groupName.trim() !== '' ? groupName : null;

            if (cleanBrand) {
                if (!brandToGroups.has(cleanBrand)) brandToGroups.set(cleanBrand, new Set());
                if (!brandToProducts.has(cleanBrand)) brandToProducts.set(cleanBrand, new Set());
                
                if (cleanGroup) brandToGroups.get(cleanBrand).add(cleanGroup);
                brandToProducts.get(cleanBrand).add(productId);
            }

            if (cleanGroup) {
                if (!groupToBrands.has(cleanGroup)) groupToBrands.set(cleanGroup, new Set());
                if (!groupToProducts.has(cleanGroup)) groupToProducts.set(cleanGroup, new Set());
                
                if (cleanBrand) groupToBrands.get(cleanGroup).add(cleanBrand);
                groupToProducts.get(cleanGroup).add(productId);
            }

            if (cleanBrand) productToBrands.set(productId, cleanBrand);
            if (cleanGroup) productToGroups.set(productId, cleanGroup);
        });

        return {
            brandToGroups,
            brandToProducts,
            groupToBrands,
            groupToProducts,
            productToBrands,
            productToGroups
        };
    };

    const getFilteredBrands = () => {
        if (!filterMaps) return availableBrands;

        let filteredBrands = new Set(availableBrands);

        if (selectedProductGroups.length > 0) {
            const brandsFromGroups = new Set();
            selectedProductGroups.forEach(group => {
                const brands = filterMaps.groupToBrands.get(group);
                if (brands) {
                    brands.forEach(brand => brandsFromGroups.add(brand));
                }
            });
            filteredBrands = new Set([...filteredBrands].filter(brand => brandsFromGroups.has(brand)));
        }

        if (selectedProductIds.length > 0) {
            const brandsFromProducts = new Set();
            selectedProductIds.forEach(productId => {
                const brand = filterMaps.productToBrands.get(productId);
                if (brand) brandsFromProducts.add(brand);
            });
            filteredBrands = new Set([...filteredBrands].filter(brand => brandsFromProducts.has(brand)));
        }

        return [...filteredBrands].sort();
    };

    const getFilteredProductGroups = () => {
        if (!filterMaps) return availableProductGroups;

        let filteredGroups = new Set(availableProductGroups);

        if (selectedBrands.length > 0) {
            const groupsFromBrands = new Set();
            selectedBrands.forEach(brand => {
                const groups = filterMaps.brandToGroups.get(brand);
                if (groups) {
                    groups.forEach(group => groupsFromBrands.add(group));
                }
            });
            filteredGroups = new Set([...filteredGroups].filter(group => groupsFromBrands.has(group)));
        }

        if (selectedProductIds.length > 0) {
            const groupsFromProducts = new Set();
            selectedProductIds.forEach(productId => {
                const group = filterMaps.productToGroups.get(productId);
                if (group) groupsFromProducts.add(group);
            });
            filteredGroups = new Set([...filteredGroups].filter(group => groupsFromProducts.has(group)));
        }

        return [...filteredGroups].sort();
    };

    const getFilteredProducts = () => {
        if (!filterMaps) return availableProducts;

        let filteredProducts = [...availableProducts];

        if (selectedBrands.length > 0) {
            const productsFromBrands = new Set();
            selectedBrands.forEach(brand => {
                const products = filterMaps.brandToProducts.get(brand);
                if (products) {
                    products.forEach(productId => productsFromBrands.add(productId));
                }
            });
            filteredProducts = filteredProducts.filter(product => productsFromBrands.has(product.productId));
        }

        if (selectedProductGroups.length > 0) {
            const productsFromGroups = new Set();
            selectedProductGroups.forEach(group => {
                const products = filterMaps.groupToProducts.get(group);
                if (products) {
                    products.forEach(productId => productsFromGroups.add(productId));
                }
            });
            filteredProducts = filteredProducts.filter(product => productsFromGroups.has(product.productId));
        }

        return filteredProducts;
    };

    const canSelectProducts = Boolean(productsUrl) || useOrganisationProducts || (selectedForecastId && selectedForecastPeriodId);

    const applyProductsToFilters = (products) => {
        const uniqueBrands = [...new Set(products
            .filter(product => product.brand && product.brand.trim() !== '')
            .map(product => product.brand))]
            .sort();
        const uniqueProductGroups = [...new Set(products
            .filter(product => product.groupName && product.groupName.trim() !== '')
            .map(product => product.groupName))]
            .sort();

        setAvailableBrands(uniqueBrands);
        setAvailableProductGroups(uniqueProductGroups);
        setAvailableProducts(products);
        setFilterMaps(createFilterMaps(products));
        onProductsLoaded?.(products);
    };

    useEffect(() => {
        if (!filtersExpanded) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (productsUrl) {
                fetchProductsFromUrl().catch(error => {
                    console.error('Failed to fetch filter options:', error);
                });
                return;
            }

            Promise.all([
                fetchBrands(true),
                fetchProductGroups(true),
                fetchProducts(true)
            ]).catch(error => {
                console.error('Failed to fetch filter options:', error);
            });
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            productsRequestController.current?.abort();
        };
    }, [filtersExpanded, selectedForecastId, selectedForecastPeriodId, selectedCustomerId, selectedChannelId, useOrganisationProducts, productsUrl]);

    useEffect(() => {
        setAvailableBrands([]);
        setAvailableProductGroups([]);
        setAvailableProducts([]);
        setFilterMaps(null);
    }, [selectedCustomerId, selectedChannelId, productsUrl]);

    const fetchForecastProducts = async () => {
        if (!((selectedCustomerId || selectedChannelId) && selectedForecastId && selectedForecastPeriodId)) {
            return null;
        }

        const params = {
            forecastPeriodId: selectedForecastPeriodId
        };
        if (selectedCustomerId) {
            params.customerId = selectedCustomerId;
        } else if (selectedChannelId) {
            params.channelId = selectedChannelId;
        }
        
        const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/products`, { params });
        return response.data;
    };

    const fetchBrands = async (forceRefresh = false) => {
        if (!forceRefresh && availableBrands.length > 0) return;
        
        try {
            setLoadingBrands(true);
            
            const forecastProducts = await fetchForecastProducts();
            if (forecastProducts) {
                const uniqueBrands = [...new Set(forecastProducts
                    .filter(product => product.brand && product.brand.trim() !== '')
                    .map(product => product.brand))]
                    .sort();
                setAvailableBrands(uniqueBrands);
            } else {
                const response = await axiosInstance.get('/products/brands');
                setAvailableBrands(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch brands:', error.response?.status, error.response?.statusText, error.message);
            setAvailableBrands([]);
        } finally {
            setLoadingBrands(false);
        }
    };

    const fetchProductGroups = async (forceRefresh = false) => {
        if (!forceRefresh && availableProductGroups.length > 0) return;
        
        try {
            setLoadingProductGroups(true);
            
            const forecastProducts = await fetchForecastProducts();
            if (forecastProducts) {
                const uniqueProductGroups = [...new Set(forecastProducts
                    .filter(product => product.groupName && product.groupName.trim() !== '')
                    .map(product => product.groupName))]
                    .sort();
                setAvailableProductGroups(uniqueProductGroups);
            } else {
                const response = await axiosInstance.get('/products/product-groups');
                setAvailableProductGroups(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch product groups:', error.response?.status, error.response?.statusText, error.message);
            setAvailableProductGroups([]);
        } finally {
            setLoadingProductGroups(false);
        }
    };

    const fetchProductsFromUrl = async () => {
        productsRequestController.current?.abort();
        const controller = new AbortController();
        productsRequestController.current = controller;

        try {
            setLoadingBrands(true);
            setLoadingProductGroups(true);
            setLoadingProducts(true);
            const response = await axiosInstance.get(productsUrl, { signal: controller.signal });
            if (!controller.signal.aborted) {
                applyProductsToFilters(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            if (controller.signal.aborted) {
                return;
            }

            console.error('Failed to fetch products:', error.response?.status, error.response?.statusText, error.message);
            setAvailableBrands([]);
            setAvailableProductGroups([]);
            setAvailableProducts([]);
            setFilterMaps(null);
            onProductsLoaded?.([]);
        } finally {
            if (!controller.signal.aborted) {
                setLoadingBrands(false);
                setLoadingProductGroups(false);
                setLoadingProducts(false);
            }
        }
    };

    const fetchProducts = async (forceRefresh = false) => {
        if (!useOrganisationProducts && (!selectedForecastId || !selectedForecastPeriodId)) return;
        if (!forceRefresh && availableProducts.length > 0) return;
        
        try {
            setLoadingProducts(true);
            const response = useOrganisationProducts
                ? await axiosInstance.get('/products')
                : await axiosInstance.get(`/forecasts/${selectedForecastId}/products`, {
                    params: {
                        forecastPeriodId: selectedForecastPeriodId,
                        ...(selectedCustomerId
                            ? { customerId: selectedCustomerId }
                            : selectedChannelId
                                ? { channelId: selectedChannelId }
                                : {})
                    }
                });
            setAvailableProducts(response.data);
            
            const maps = createFilterMaps(response.data);
            setFilterMaps(maps);
            onProductsLoaded?.(response.data);
        } catch (error) {
            console.error('Failed to fetch products:', error.response?.status, error.response?.statusText, error.message);
            setAvailableProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleBrandChange = (event, values) => {
        const uniqueBrands = [...new Set([...selectedBrands, ...values])];
        setSelectedBrands(uniqueBrands);
    };

    const handleProductGroupChange = (event, values) => {
        const uniqueGroups = [...new Set([...selectedProductGroups, ...values])];
        setSelectedProductGroups(uniqueGroups);
    };

    const handleProductChange = (event, values) => {
        const newProductIds = values.map(p => p.productId);
        const uniqueIds = [...new Set([...selectedProductIds, ...newProductIds])];
        setSelectedProductIds(uniqueIds);
    };

    const getProductNameById = (productId) => {
        const product = availableProducts.find(p => p.productId === productId);
        return product ? `${product.skuNumber} - ${product.skuDescription}` : `Product ${productId}`;
    };

    return (
        <>
            <Collapse in={filtersExpanded}>
                <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                    {isFilterDataLoading() && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Loading filter options...
                            </Typography>
                            <LinearProgress />
                        </Box>
                    )}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            {isFilterDataLoading() && availableBrands.length === 0 ? (
                                <Skeleton variant="rectangular" height={56} />
                            ) : (
                                <Autocomplete
                                    multiple
                                    options={getFilteredBrands().filter(brand => !selectedBrands.includes(brand))}
                                    loading={loadingBrands}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Brand"
                                            placeholder={loadingBrands ? "Loading brands..." : "Brand"}
                                        />
                                    )}
                                    onChange={handleBrandChange}
                                    value={[]}
                                    disabled={loadingBrands}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {isFilterDataLoading() && availableProductGroups.length === 0 ? (
                                <Skeleton variant="rectangular" height={56} />
                            ) : (
                                <Autocomplete
                                    multiple
                                    options={getFilteredProductGroups().filter(group => !selectedProductGroups.includes(group))}
                                    loading={loadingProductGroups}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Group"
                                            placeholder={loadingProductGroups ? "Loading groups..." : "Group"}
                                        />
                                    )}
                                    onChange={handleProductGroupChange}
                                    value={[]}
                                    disabled={loadingProductGroups}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {isFilterDataLoading() && availableProducts.length === 0 && canSelectProducts ? (
                                <Skeleton variant="rectangular" height={56} />
                            ) : (
                                <Autocomplete
                                    multiple
                                    options={getFilteredProducts().filter(product => !selectedProductIds.includes(product.productId))}
                                    loading={loadingProducts}
                                    getOptionLabel={(option) => `${option.skuNumber} - ${option.skuDescription}`}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="SKU"
                                            placeholder={
                                                !canSelectProducts
                                                    ? "Select forecast and period first..." 
                                                    : loadingProducts 
                                                        ? "Loading SKUs..." 
                                                        : "SKU"
                                            }
                                        />
                                    )}
                                    onChange={handleProductChange}
                                    value={[]}
                                    disabled={!canSelectProducts || loadingProducts}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            {hasActiveFilters() && (
                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {selectedBrands.length > 0 && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={clearBrands}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                >
                                    Clear Brands
                                </Button>
                            )}
                            {selectedProductGroups.length > 0 && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="secondary"
                                    startIcon={<ClearIcon />}
                                    onClick={clearProductGroups}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                >
                                    Clear Groups
                                </Button>
                            )}
                            {selectedProductIds.length > 0 && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="success"
                                    startIcon={<ClearIcon />}
                                    onClick={clearSkus}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                >
                                    Clear SKUs
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={clearAllFilters}
                                size="small"
                                sx={{ ml: 1 }}
                            >
                                Clear All
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedBrands.map((brand) => (
                            <Chip
                                key={`brand-${brand}`}
                                label={brand}
                                variant="outlined"
                                size="small"
                                onDelete={() => removeFilter('brand', brand)}
                                color="primary"
                            />
                        ))}
                        {selectedProductGroups.map((group) => (
                            <Chip
                                key={`group-${group}`}
                                label={group}
                                variant="outlined"
                                size="small"
                                onDelete={() => removeFilter('productGroup', group)}
                                color="secondary"
                            />
                        ))}
                        {selectedProductIds.map((productId) => (
                            <Chip
                                key={`product-${productId}`}
                                label={getProductNameById(productId)}
                                variant="outlined"
                                size="small"
                                onDelete={() => removeFilter('product', productId)}
                                color="success"
                            />
                        ))}
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default ProductFilters;
